/**
 * @file Étape de sélection du service (midi/soir) et du nombre de convives.
 * @module packages.common.components.table-reservation.sections.ServiceGuestsStep
 *
 * Première étape du parcours de réservation. Affiche les services disponibles
 * sous forme de toggle buttons et un compteur de convives (pattern spinbutton ARIA).
 * Lorsque le nombre maximum de convives est atteint, une bannière invite le client
 * à contacter l'établissement par téléphone.
 */

import React, {useState, useEffect, useMemo, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';

// icons
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import PersonIcon from '@mui/icons-material/Person';

// components
import ServiceToggleButton from '../components/ServiceToggleButton';
import AnnouncementBanner from '../components/AnnouncementBanner';
import WelcomeMessage from '../components/WelcomeMessage';
import OpeningBanner from '../components/OpeningBanner';
import {useLocales} from 'packages/common/locales';

// Accessibility
import {useAnnounce} from 'packages/common/hooks/useAccessibility';

// constants
import {SERVICE_TYPES, ERROR_MESSAGES} from '../constants/reservation';

// ----------------------------------------------------------------------

const CounterContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  width: '100%',
}));

const CounterButton = styled(IconButton)(({theme}) => ({
  'backgroundColor': alpha(theme.palette.primary.main, 0.08),
  'color': theme.palette.primary.main,
  'margin': theme.spacing(0, 2),
  'transition': theme.transitions.create(['transform', 'box-shadow']),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.25)}`,
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
  '&.Mui-disabled': {
    color: theme.palette.text.disabled,
    backgroundColor: alpha(theme.palette.divider, 0.3),
  },
}));

const CounterTextField = styled(TextField)(({theme}) => ({
  '& .MuiInputBase-input': {
    textAlign: 'center',
    width: '100%',
    padding: theme.spacing(1),
    fontSize: '16px', // 16px minimum pour éviter le zoom automatique sur iOS/Safari
    fontWeight: 'bold',
  },
  '& .MuiOutlinedInput-root': {
    width: '90px',
  },
  '& .MuiInputAdornment-root': {
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  },
}));

// ----------------------------------------------------------------------

/**
 * Étape combinée de sélection du service et du nombre de convives.
 *
 * @param {Object} props
 * @param {Object} props.formData - Données du formulaire (service, partySize)
 * @param {Function} props.updateFormData - Met à jour un champ du formulaire
 * @param {Object} [props.config] - Configuration du restaurant (services, contraintes, annonces)
 * @param {Function} props.setValidation - Enregistre la fonction de validation
 * @param {Function} props.setErrors - Définit les erreurs de validation
 * @param {Object} [props.errors] - Erreurs de validation courantes
 * @param {Function} [props.setStepReady] - Active/désactive le bouton suivant
 * @param {Object} [props.companyDetails] - Informations de l'établissement (logo, nom)
 * @param {string} [props.primaryColor] - Couleur d'accentuation du widget
 * @param {Function} [props.t] - Fonction de traduction générique
 * @param {Function} [props.getCustomText] - Fonction de traduction des textes personnalisés
 * @return {JSX.Element}
 */
export function ServiceGuestsStep({
  formData,
  updateFormData,
  config,
  setValidation,
  setErrors,
  errors,
  setStepReady,
  companyDetails,
  primaryColor,
  onContentResize = () => {},
  t,
  getCustomText,
}) {
  // État local pour mise à jour immédiate de l'UI sans attendre le cycle React du parent
  const [localPartySize, setLocalPartySize] = useState(formData.partySize);

  const counterContainerRef = useRef(null);
  const announce = useAnnounce();

  const constraints = useMemo(() => {
    if (!config) {
      return {minGuests: 1, maxGuests: 10};
    }
    return {
      minGuests: config.reservationConstraints?.minGuests || 1,
      maxGuests: config.reservationConstraints?.onlineBooking?.maxGuests || 10,
    };
  }, [config]);

  const maxGuestsMessage = getCustomText ?
    (getCustomText('onlineBookingMessage') || 'Pour les groupes plus nombreux, veuillez nous contacter par téléphone.') :
    'Pour les groupes plus nombreux, veuillez nous contacter par téléphone.';

  const availableServices = useMemo(() => {
    if (!config?.availableServices || config.availableServices.length === 0) {
      return [SERVICE_TYPES.LUNCH, SERVICE_TYPES.DINNER];
    }
    return config.availableServices;
  }, [config]);

  useEffect(() => {
    if (availableServices.length === 1 && !formData.service) {
      updateFormData('service', availableServices[0]);
    }
  }, [availableServices, formData.service, updateFormData]);

  const validateStep = useCallback(() => {
    if (!formData.service) {
      setErrors({service: ERROR_MESSAGES.SERVICE_REQUIRED});
      return false;
    }

    if (localPartySize < constraints.minGuests) {
      // eslint-disable-next-line new-cap
      setErrors({partySize: ERROR_MESSAGES.PARTY_SIZE_MIN(constraints.minGuests)});
      return false;
    }

    if (localPartySize > constraints.maxGuests) {
      // eslint-disable-next-line new-cap
      setErrors({partySize: ERROR_MESSAGES.PARTY_SIZE_MAX(constraints.maxGuests)});
      return false;
    }

    return true;
  }, [formData.service, localPartySize, constraints.minGuests, constraints.maxGuests, setErrors]);

  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, validateStep, formData.service, localPartySize, constraints.minGuests, constraints.maxGuests]);

  useEffect(() => {
    if (setStepReady) {
      const isServiceSelected = !!formData.service;
      const isPartySizeValid = localPartySize >= constraints.minGuests && localPartySize <= constraints.maxGuests;

      setStepReady(isServiceSelected && isPartySizeValid);
    }
  }, [formData.service, localPartySize, constraints.minGuests, constraints.maxGuests, setStepReady]);

  const handleServiceSelection = (_, serviceType) => {
    if (serviceType !== null) {
      updateFormData('service', serviceType);
      if (errors.service) {
        setErrors({});
      }
    }
  };

  const handleIncrement = useCallback(() => {
    if (localPartySize < constraints.maxGuests) {
      const newValue = localPartySize + 1;
      setLocalPartySize(newValue);
      updateFormData('partySize', newValue);
      announce(`${newValue} ${newValue > 1 ? 'personnes' : 'personne'}`);

      if (errors.partySize) {
        setErrors({});
      }
    }
  }, [localPartySize, constraints.maxGuests, updateFormData, announce, errors.partySize, setErrors]);

  const handleDecrement = useCallback(() => {
    if (localPartySize > 0) {
      const newValue = localPartySize - 1;
      setLocalPartySize(newValue);
      updateFormData('partySize', newValue);
      announce(`${newValue} ${newValue > 1 ? 'personnes' : 'personne'}`);

      if (errors.partySize) {
        setErrors({});
      }
    }
  }, [localPartySize, updateFormData, announce, errors.partySize, setErrors]);

  // Navigation clavier conforme au pattern ARIA spinbutton (fleches, Home, End)
  const handleCounterKeyDown = useCallback((event) => {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowRight':
        event.preventDefault();
        event.stopPropagation();
        handleIncrement();
        break;
      case 'ArrowDown':
      case 'ArrowLeft':
        event.preventDefault();
        event.stopPropagation();
        handleDecrement();
        break;
      case 'Home':
        event.preventDefault();
        event.stopPropagation();
        if (localPartySize !== 0) {
          setLocalPartySize(0);
          updateFormData('partySize', 0);
          announce('0 personne');
        }
        break;
      case 'End':
        event.preventDefault();
        event.stopPropagation();
        if (localPartySize !== constraints.maxGuests) {
          setLocalPartySize(constraints.maxGuests);
          updateFormData('partySize', constraints.maxGuests);
          announce(`${constraints.maxGuests} personnes`);
        }
        break;
      default:
        break;
    }
  }, [handleIncrement, handleDecrement, localPartySize, constraints.maxGuests, updateFormData, announce]);

  const handleInputChange = (event) => {
    const value = parseInt(event.target.value, 10);

    if (isNaN(value)) {
      setLocalPartySize(0);
      updateFormData('partySize', 0);
      return;
    }

    const newValue = Math.min(Math.max(0, value), constraints.maxGuests);
    setLocalPartySize(newValue);
    updateFormData('partySize', newValue);

    if (errors.partySize) {
      setErrors({});
    }
  };

  const showMaxGuestsMessage = localPartySize >= constraints.maxGuests;

  useEffect(() => {
    onContentResize();
  }, [showMaxGuestsMessage, onContentResize]);

  // Avant-ouverture d'un nouveau restaurant : annoncée dès l'arrivée, avant le choix du service.
  const {currentLang} = useLocales();
  const preOpening = config?.preOpening || null;

  const welcomeMessage = getCustomText ? getCustomText('welcomeMessage') : '';
  const announcementText = getCustomText ? getCustomText('announcement.text') : '';

  const announcement = config?.announcement && announcementText ? {
    ...config.announcement,
    text: announcementText,
  } : null;

  return (
    <Fade in timeout={300}>
      <Box>
        {config?.showWelcomeMessage && (
          <WelcomeMessage
            message={welcomeMessage}
            showLogo={config?.showLogoInWelcomeMessage}
            companyLogo={companyDetails?.logoPath}
            companyName={companyDetails?.name}
          />
        )}

        {announcement && <AnnouncementBanner announcement={announcement} />}

        {t && preOpening && (
          <Box sx={{mb: 2}}>
            <OpeningBanner preOpening={preOpening} restaurantName={companyDetails?.name} dateFnsLocale={currentLang?.adapterLocale} t={t} />
          </Box>
        )}

        {availableServices.length > 1 && (
          <>
            <Typography variant="h6" sx={{fontWeight: 600, mb: 3, textAlign: 'center'}}>
              {t ? t('widgets.reservation.form.serviceQuestion') : 'Quel service souhaitez-vous réserver ?'}
            </Typography>

            <ToggleButtonGroup
              value={formData.service}
              exclusive
              onChange={handleServiceSelection}
              aria-label="service selection"
              fullWidth
              sx={{
                'border': 'none',
                'mb': 3,
              }}
            >
              {availableServices.includes(SERVICE_TYPES.LUNCH) && (
                <ServiceToggleButton
                  value={SERVICE_TYPES.LUNCH}
                  serviceType={SERVICE_TYPES.LUNCH}
                  aria-label="déjeuner"
                >
                  <Typography variant="body2">
                    {t ? t('widgets.reservation.form.lunchService') : 'Midi'}
                  </Typography>
                </ServiceToggleButton>
              )}

              {availableServices.includes(SERVICE_TYPES.DINNER) && (
                <ServiceToggleButton
                  value={SERVICE_TYPES.DINNER}
                  serviceType={SERVICE_TYPES.DINNER}
                  aria-label="dîner"
                >
                  <Typography variant="body2">
                    {t ? t('widgets.reservation.form.dinnerService') : 'Soir'}
                  </Typography>
                </ServiceToggleButton>
              )}
            </ToggleButtonGroup>

            {errors.service && (
              <Alert severity="error" sx={{mb: 2}} role="alert">
                {errors.service}
              </Alert>
            )}
          </>
        )}

        <Typography variant="body1" sx={{mb: 2, textAlign: 'center', fontWeight: 'medium'}}>
          {t ? t('widgets.reservation.form.guestsQuestion') : 'Pour combien de personnes ?'}
        </Typography>

        <CounterContainer
          ref={counterContainerRef}
          role="spinbutton"
          aria-valuenow={localPartySize}
          aria-valuemin={0}
          aria-valuemax={constraints.maxGuests}
          aria-label={t ? t('widgets.reservation.form.numberOfPeople') : 'Nombre de personnes'}
          tabIndex={0}
          onKeyDown={handleCounterKeyDown}
          sx={{
            '&:focus': {outline: 'none'},
            '&:focus-visible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: '4px',
              borderRadius: '8px',
            },
          }}
        >
          <CounterButton
            onClick={handleDecrement}
            disabled={localPartySize <= 0}
            size="medium"
            aria-hidden="true"
            tabIndex={-1}
          >
            <RemoveIcon aria-hidden="true" />
          </CounterButton>

          <CounterTextField
            value={localPartySize}
            onChange={handleInputChange}
            variant="outlined"
            InputProps={{
              readOnly: false,
              endAdornment: (
                <InputAdornment position="end">
                  <PersonIcon fontSize="medium" aria-hidden="true" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              'min': 0,
              'max': constraints.maxGuests,
              'aria-hidden': 'true',
              'tabIndex': -1,
              'inputMode': 'numeric',
              'pattern': '[0-9]*',
            }}
          />

          <CounterButton
            onClick={handleIncrement}
            disabled={localPartySize >= constraints.maxGuests}
            size="medium"
            aria-hidden="true"
            tabIndex={-1}
          >
            <AddIcon aria-hidden="true" />
          </CounterButton>
        </CounterContainer>

        {showMaxGuestsMessage && (
          <Box sx={{width: '100%', mb: 2}}>
            <Box
              sx={(theme) => ({
                'position': 'relative',
                'borderRadius': 1,
                'border': `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                'backgroundColor': alpha(theme.palette.info.main, 0.05),
                'py': 2,
                'px': 3,
                'textAlign': 'center',
              })}
            >
              <Box
                sx={(theme) => ({
                  'position': 'absolute',
                  'top': '-12px',
                  'left': '50%',
                  'marginLeft': '-12px',
                  'width': '24px',
                  'height': '24px',
                  'borderRadius': '50%',
                  'backgroundColor': theme.palette.info.main,
                  'color': 'white',
                  'display': 'flex',
                  'alignItems': 'center',
                  'justifyContent': 'center',
                  'boxShadow': '0 2px 4px rgba(0,0,0,0.15)',
                })}
              >
                <InfoIcon sx={{fontSize: '14px'}} />
              </Box>

              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.5,
                  color: 'info.main',
                }}
              >
                {maxGuestsMessage}
              </Typography>
            </Box>
          </Box>
        )}

        {errors.partySize && (
          <Alert severity="error" sx={{mt: 2}} role="alert">
            {errors.partySize}
          </Alert>
        )}

      </Box>
    </Fade>
  );
}

ServiceGuestsStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
  companyDetails: PropTypes.object,
  primaryColor: PropTypes.string,
  onContentResize: PropTypes.func,
  t: PropTypes.func,
  getCustomText: PropTypes.func,
};
